import type {
  AdminAccount,
  ApiHealth,
  ApiPage,
  AuditEvent,
  AuditEventSummary,
  AuthContext,
  DeerMap,
  Dataset,
  EditionCapabilities,
  MergeRequest,
  MergeRequestDetail,
  MergeRequestListPage,
  SurveyMergeRequestPage,
  SurveyMergeRequestDetail,
  MergeReviewCloseResult,
  MergeReviewProgress,
  MapViewportQuery,
  PatternAsset,
  PatternAssetUpload,
  PublishRecord,
  SurveyFeature,
  SurveyLogicalObject,
  SurveyState,
  SurveyStyle,
  SurveyStyleDeleteResult,
  WorkspaceState,
  MergeReviewDecision,
  WorldDescriptor,
  WorldSettingsPayload,
  ForceRenderRequest,
  ForceRenderResponse,
  MapRenderStatus,
  MapFreshness,
  RenderingStatus,
  BuildingFloor,
} from '../types/domain'

export const API_SCHEMA_VERSION = '1'
export const READ_REQUEST_TIMEOUT_MS = 15_000

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

const apiOrigin = String(import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '')
const tileOrigin = String(import.meta.env.VITE_TILE_BASE_URL || apiOrigin.replace(/\/api\/v1$/, '')).replace(/\/$/, '')
let csrfTokenValue: string | null = null

const asRecord = (value: unknown): Record<string, unknown> => value && typeof value === 'object' ? value as Record<string, unknown> : {}

export const isAbortError = (error: unknown): boolean => Boolean(
  error && typeof error === 'object' && 'name' in error && (error as { name?: unknown }).name === 'AbortError',
)

const csrfToken = () => {
  if (typeof document === 'undefined') return csrfTokenValue
  const prefix = 'deermap-csrf='
  const value = document.cookie.split(';').map((item) => item.trim()).find((item) => item.startsWith(prefix))
  return value ? decodeURIComponent(value.slice(prefix.length)) : csrfTokenValue
}

const parseError = async (response: Response): Promise<ApiError> => {
  let payload: Record<string, unknown> = {}
  try {
    payload = asRecord(await response.json())
  } catch (error) {
    if (isAbortError(error)) throw error
    // A proxy or server error can be an HTML response. Keep the status useful.
  }
  if (payload.schemaVersion !== undefined && String(payload.schemaVersion) !== API_SCHEMA_VERSION) {
    return new ApiError(response.status, 'SCHEMA_VERSION_UNSUPPORTED', `不支持的 DeerMap API schema 版本：${String(payload.schemaVersion)}`)
  }
  const errorPayload = asRecord(payload.error || payload)
  return new ApiError(
    response.status,
    String(errorPayload.code || 'HTTP_ERROR'),
    String(errorPayload.message || `DeerMap API returned ${response.status}`),
    asRecord(errorPayload.details),
  )
}

export class ApiClient {
  private authPromise: Promise<AuthContext> | null = null
  private capabilitiesPromise: Promise<EditionCapabilities> | null = null

  private clearSessionCache() {
    this.authPromise = null
    this.capabilitiesPromise = null
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    // Bound the complete read, including a response body that never finishes.
    // Mutations retain their existing reconciliation semantics: no timeout or
    // automatic replay is introduced for a potentially committed write.
    if (!['GET', 'HEAD', 'OPTIONS'].includes((init.method || 'GET').toUpperCase())) {
      return this.performRequest<T>(path, init)
    }
    const controller = new AbortController()
    const external = init.signal
    const abort = () => controller.abort()
    if (external?.aborted) throw new DOMException('Request cancelled', 'AbortError')
    external?.addEventListener('abort', abort, { once: true })
    let timedOut = false
    const timer = setTimeout(() => { timedOut = true; controller.abort() }, READ_REQUEST_TIMEOUT_MS)
    try {
      return await this.performRequest<T>(path, { ...init, signal: controller.signal })
    } catch (error) {
      if (external?.aborted) throw new DOMException('Request cancelled', 'AbortError')
      if (timedOut) throw new ApiError(0, 'REQUEST_TIMEOUT', '读取地图数据超时，请重试。')
      throw error
    } finally {
      clearTimeout(timer)
      external?.removeEventListener('abort', abort)
    }
  }

  private async performRequest<T>(path: string, init: RequestInit): Promise<T> {
    const headers = new Headers(init.headers)
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
    if (!headers.has('Accept')) headers.set('Accept', 'application/json')
    if (init.method && !['GET', 'HEAD', 'OPTIONS'].includes(init.method.toUpperCase())) {
      const token = csrfToken()
      if (token) headers.set('X-CSRF-Token', token)
    }
    let response: Response
    try {
      response = await fetch(`${apiOrigin}${path}`, {
        ...init,
        cache: init.cache ?? 'no-store',
        headers,
        credentials: 'include',
      })
    } catch (error) {
      if (isAbortError(error)) throw error
      throw new ApiError(0, 'NETWORK_ERROR', '无法连接 DeerMap API。')
    }
    const responseToken = response.headers.get('X-CSRF-Token')
    if (responseToken) csrfTokenValue = responseToken
    if (!response.ok) {
      const error = await parseError(response)
      if (response.status === 401 || response.status === 403
        || error.code === 'AUTHENTICATION_REQUIRED' || error.code === 'AUTHORIZATION_REQUIRED') this.clearSessionCache()
      throw error
    }
    if (response.status === 204) return undefined as T
    let payload: unknown
    try {
      payload = await response.json() as unknown
    } catch (error) {
      if (isAbortError(error)) throw error
      throw new ApiError(response.status, 'INVALID_RESPONSE', 'DeerMap API 返回了无效 JSON。')
    }
    if (!payload || typeof payload !== 'object' || !('schemaVersion' in payload)) {
      throw new ApiError(response.status, 'INVALID_RESPONSE', 'DeerMap API 响应缺少 schema 封装。')
    }
    const envelope = payload as { schemaVersion?: unknown; data?: T; error?: unknown }
    if (String(envelope.schemaVersion) !== API_SCHEMA_VERSION) {
      throw new ApiError(response.status, 'SCHEMA_VERSION_UNSUPPORTED', `不支持的 DeerMap API schema 版本：${String(envelope.schemaVersion)}`)
    }
    if (envelope.error) {
      const error = asRecord(envelope.error)
      const apiError = new ApiError(response.status, String(error.code || 'API_ERROR'), String(error.message || 'DeerMap API 返回了错误。'), asRecord(error.details))
      if (apiError.code === 'AUTHENTICATION_REQUIRED' || apiError.code === 'AUTHORIZATION_REQUIRED') this.clearSessionCache()
      throw apiError
    }
    if (!('data' in envelope)) {
      throw new ApiError(response.status, 'INVALID_RESPONSE', 'DeerMap API 响应缺少数据封装。')
    }
    return envelope.data as T
  }

  get health() {
    return this.request<ApiHealth>('/health')
  }

  readHealth(signal?: AbortSignal) { return this.request<ApiHealth>('/health', { signal }) }
  readWorlds(signal?: AbortSignal) { return this.request<WorldDescriptor[]>('/worlds', { signal }) }
  readDatasets(signal?: AbortSignal) { return this.request<Dataset[]>('/datasets', { signal }) }
  readCapabilities(signal?: AbortSignal) { return this.request<EditionCapabilities>('/capabilities', { signal }) }

  get capabilities() {
    if (!this.capabilitiesPromise) {
      this.capabilitiesPromise = this.request<EditionCapabilities>('/capabilities').catch((error) => {
        this.capabilitiesPromise = null
        throw error
      })
    }
    return this.capabilitiesPromise
  }

  get worlds() {
    return this.request<WorldDescriptor[]>('/worlds')
  }

  updateWorldSettings(worldId: string, payload: WorldSettingsPayload) {
    return this.request<WorldDescriptor>(`/survey/worlds/${encodeURIComponent(worldId)}/settings`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  maps() {
    return this.request<DeerMap[]>('/admin/maps')
  }

  mapRenderStatuses(signal?: AbortSignal) {
    return this.request<MapRenderStatus[]>('/admin/maps/render-status', { signal })
  }

  renderingStatus(signal?: AbortSignal) {
    return this.request<RenderingStatus>('/admin/rendering/status', { signal })
  }

  forceRenderMap(mapId: string, payload: ForceRenderRequest) {
    return this.request<ForceRenderResponse>(`/admin/maps/${encodeURIComponent(mapId)}/force-render`, {
      method: 'POST', body: JSON.stringify(payload),
    })
  }

  mapFreshness(mapId: string, bounds: Pick<MapViewportQuery, 'minX' | 'minZ' | 'maxX' | 'maxZ'>, signal?: AbortSignal) {
    const query = new URLSearchParams(Object.entries(bounds).map(([key, value]) => [key, String(value)]))
    return this.request<MapFreshness>(`/maps/${encodeURIComponent(mapId)}/freshness?${query}`, { signal })
  }

  prioritizeMapRegion(mapId: string, bounds: Pick<MapViewportQuery, 'minX' | 'minZ' | 'maxX' | 'maxZ'>) {
    return this.request<{ mapId: string; promotedCount: number; limit: number; capturedAt: string }>(`/admin/maps/${encodeURIComponent(mapId)}/prioritize`, {
      method: 'POST', body: JSON.stringify(bounds),
    })
  }

  createMap(payload: { worldId: string; dimensionId: string; name: string; icon: string; scanStartY: number | null; rendering: unknown }) {
    return this.request<DeerMap>('/admin/maps', { method: 'POST', body: JSON.stringify(payload) })
  }

  updateMapSettings(mapId: string, payload: { name: string; icon: string; scanStartY: number | null; rendering: unknown }) {
    return this.request<DeerMap>(`/admin/maps/${encodeURIComponent(mapId)}/settings`, {
      method: 'PUT', body: JSON.stringify(payload),
    })
  }

  deleteMap(mapId: string, mode: 'DELETE_SURVEY_DATA' | 'TRANSFER_SURVEY_DATA', targetMapId?: string) {
    return this.request<Record<string, unknown>>(`/admin/maps/${encodeURIComponent(mapId)}`, {
      method: 'DELETE', body: JSON.stringify({ mode, ...(targetMapId ? { targetMapId } : {}) }),
    })
  }

  transferMapData(mapId: string, targetMapId: string) {
    return this.request<Record<string, unknown>>(`/admin/maps/${encodeURIComponent(mapId)}/transfer-data`, {
      method: 'POST', body: JSON.stringify({ targetMapId }),
    })
  }

  get datasets() {
    return this.request<Dataset[]>('/datasets')
  }

  search(query: string, datasetId = 'builtin-survey', page = 0, pageSize = 20, mapId?: string, signal?: AbortSignal) {
    const params = new URLSearchParams({ q: query, datasetId, page: String(page), pageSize: String(pageSize), ...(mapId ? { mapId } : {}) })
    return this.request<ApiPage<Record<string, unknown>>>(`/search?${params.toString()}`, { signal })
  }

  publishedSurvey(datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    return this.request<Record<string, unknown>>(mapId
      ? `/surveys/${encodeURIComponent(mapId)}/${encodeURIComponent(datasetId)}`
      : `/surveys/${encodeURIComponent(datasetId)}`, { signal })
  }

  publishedObject(objectId: string, scope: {
    datasetId: string; worldId: string; dimensionId: string; mapId?: string; includeUnderground?: boolean
  }, signal?: AbortSignal) {
    const params = new URLSearchParams({ datasetId: scope.datasetId,
      ...(scope.includeUnderground ? { includeUnderground: 'true' } : {}) })
    const path = scope.mapId
      ? `/maps/${encodeURIComponent(scope.mapId)}`
      : `/maps/${encodeURIComponent(scope.worldId)}/${encodeURIComponent(scope.dimensionId)}`
    return this.request<Record<string, unknown>>(`${path}/survey/objects/${encodeURIComponent(objectId)}?${params}`, { signal })
  }

  surveyViewport(worldId: string, dimensionId: string, viewport: MapViewportQuery, datasetId = 'builtin-survey', includeUnderground = false, mapId?: string, signal?: AbortSignal) {
    const params = new URLSearchParams({
      datasetId,
      minX: String(Math.round(viewport.minX)),
      minZ: String(Math.round(viewport.minZ)),
      maxX: String(Math.round(viewport.maxX)),
      maxZ: String(Math.round(viewport.maxZ)),
      zoom: String(viewport.zoom),
      maxObjects: String(viewport.maxObjects ?? 500),
      ...(includeUnderground ? { includeUnderground: 'true' } : {}), ...(mapId ? { mapId } : {}),
    })
    return this.request<SurveyState>(mapId
      ? `/maps/${encodeURIComponent(mapId)}/survey?${params.toString()}`
      : `/maps/${encodeURIComponent(worldId)}/${encodeURIComponent(dimensionId)}/survey?${params.toString()}`, { signal })
  }

  mapTileUrl(mapId: string, sampleFactor = 1, x = 0, z = 0) {
    return `${tileOrigin}/maps/${encodeURIComponent(mapId)}/tiles/${encodeURIComponent(String(sampleFactor))}/${encodeURIComponent(String(x))}/${encodeURIComponent(String(z))}.webp`
  }

  login(username: string, password: string) {
    return this.request<AuthContext>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }).then((context) => {
      this.authPromise = Promise.resolve(context)
      this.capabilitiesPromise = null
      return context
    })
  }

  redeemToken(token: string) {
    return this.request<AuthContext>('/auth/token', { method: 'POST', body: JSON.stringify({ token }) }).then((context) => {
      this.authPromise = Promise.resolve(context)
      this.capabilitiesPromise = null
      return context
    })
  }

  me() {
    if (!this.authPromise) {
      this.authPromise = this.request<AuthContext>('/auth/me').catch((error) => {
        this.authPromise = null
        throw error
      })
    }
    return this.authPromise
  }

  logout() {
    return this.request<{ loggedOut: boolean }>('/auth/logout', { method: 'POST' }).finally(() => {
      this.clearSessionCache()
    })
  }

  surveyState(datasetId = 'builtin-survey', mapId?: string) {
    const params = new URLSearchParams({ datasetId, ...(mapId ? { mapId } : {}) })
    return this.request<SurveyState>(`/survey/state?${params.toString()}`)
  }

  adminMainSurveyState(datasetId = 'builtin-survey', mapId?: string) {
    const params = new URLSearchParams({ datasetId, ...(mapId ? { mapId } : {}) })
    return this.request<SurveyState>(`/admin/survey/main-state?${params.toString()}`)
  }

  workspace(datasetId = 'builtin-survey', mapId?: string) {
    const params = new URLSearchParams({ datasetId, ...(mapId ? { mapId } : {}) })
    return this.request<WorkspaceState>(`/survey/workspace?${params.toString()}`)
  }

  updateWorkspaceFromMain(datasetId = 'builtin-survey', mapId?: string) {
    return this.request<WorkspaceState>('/survey/workspace/update', {
      method: 'POST', body: JSON.stringify({ datasetId, ...(mapId ? { mapId } : {}) }),
    })
  }

  forceSyncWorkspace(datasetId = 'builtin-survey', mapId?: string) {
    return this.request<WorkspaceState>('/survey/workspace/force-sync', {
      method: 'POST', body: JSON.stringify({ datasetId, ...(mapId ? { mapId } : {}) }),
    })
  }

  saveDraft(draft: SurveyState, expectedDraftRevision: number, datasetId = 'builtin-survey', mapId?: string) {
    return this.request<WorkspaceState>('/survey/draft', {
      method: 'PUT', body: JSON.stringify({ datasetId, expectedDraftRevision, draft, ...(mapId ? { mapId } : {}) }),
    })
  }

  upsertFeature(feature: SurveyFeature, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    return this.request<SurveyFeature>('/survey/features', {
      method: 'POST',
      body: JSON.stringify({ datasetId, ...(mapId ? { mapId } : {}), ...feature }),
      signal,
    })
  }

  deleteFeature(featureId: string, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    const params = new URLSearchParams({ datasetId, ...(mapId ? { mapId } : {}) })
    return this.request<{ deleted: boolean }>(`/survey/features/${encodeURIComponent(featureId)}?${params.toString()}`, { method: 'DELETE', signal })
  }

  createLogicalObject(payload: Pick<SurveyLogicalObject, 'id' | 'memberKind' | 'memberIds' | 'placementContext' | 'properties' | 'floors'>, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    return this.request<SurveyLogicalObject>('/survey/logical-objects', { method: 'POST', body: JSON.stringify({ datasetId, ...(mapId ? { mapId } : {}), ...payload }), signal })
  }

  addLogicalMembers(logicalId: string, memberIds: string[], datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    return this.request<SurveyLogicalObject>(`/survey/logical-objects/${encodeURIComponent(logicalId)}/members`, { method: 'POST', body: JSON.stringify({ datasetId, memberIds, ...(mapId ? { mapId } : {}) }), signal })
  }

  updateLogicalObject(logicalId: string, payload: Pick<SurveyLogicalObject, 'placementContext' | 'properties'>, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    return this.request<SurveyLogicalObject>(`/survey/logical-objects/${encodeURIComponent(logicalId)}`, {
      method: 'PUT', body: JSON.stringify({ datasetId, ...(mapId ? { mapId } : {}), ...payload }), signal,
    })
  }

  removeLogicalMember(logicalId: string, memberId: string, deleteMember = false, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    const params = new URLSearchParams({ datasetId, delete: String(deleteMember), ...(mapId ? { mapId } : {}) })
    return this.request<{ updated: boolean }>(`/survey/logical-objects/${encodeURIComponent(logicalId)}/members/${encodeURIComponent(memberId)}?${params.toString()}`, { method: 'DELETE', signal })
  }

  dissolveLogicalObject(logicalId: string, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    const params = new URLSearchParams({ datasetId, ...(mapId ? { mapId } : {}) })
    return this.request<{ dissolved: boolean }>(`/survey/logical-objects/${encodeURIComponent(logicalId)}?${params.toString()}`, { method: 'DELETE', signal })
  }

  saveFloor(targetId: string, floor: unknown, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    return this.request<unknown>('/survey/floors', { method: 'PUT', body: JSON.stringify({ datasetId, targetId, floor, ...(mapId ? { mapId } : {}) }), signal })
  }

  replaceFloors(targetId: string, floors: BuildingFloor[], datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    return this.request<BuildingFloor[]>('/survey/floors', {
      method: 'PUT',
      body: JSON.stringify({ datasetId, targetId, floors, ...(mapId ? { mapId } : {}) }),
      signal,
    })
  }

  mergeRequests(datasetId = 'builtin-survey', mapId?: string) {
    const params = new URLSearchParams({ datasetId, ...(mapId ? { mapId } : {}) })
    return this.request<MergeRequest[]>(`/survey/merge-requests?${params.toString()}`)
  }

  surveyMergeRequestSummaries(page = 0, pageSize = 20, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    const params = new URLSearchParams({ datasetId, page: String(page), pageSize: String(pageSize), ...(mapId ? { mapId } : {}) })
    return this.request<SurveyMergeRequestPage>(`/survey/merge-requests/summaries?${params.toString()}`, { signal })
  }

  surveyMergeRequest(id: string, datasetId = 'builtin-survey', mapId?: string, signal?: AbortSignal) {
    const params = new URLSearchParams({ datasetId, ...(mapId ? { mapId } : {}) })
    return this.request<SurveyMergeRequestDetail>(`/survey/merge-requests/${encodeURIComponent(id)}?${params.toString()}`, { signal })
  }

  adminMergeRequests(view: 'pending' | 'history' = 'pending', query = '', page = 0, pageSize = 30, signal?: AbortSignal) {
    const params = new URLSearchParams({ view, q: query, page: String(page), pageSize: String(pageSize) })
    return this.request<MergeRequestListPage>(`/admin/merge-requests?${params.toString()}`, { signal })
  }

  adminMergeRequest(id: string, mapId?: string) {
    const suffix = mapId ? `?mapId=${encodeURIComponent(mapId)}` : ''
    return this.request<MergeRequestDetail>(`/admin/merge-requests/${encodeURIComponent(id)}${suffix}`)
  }

  saveMergeDecisions(id: string, decisions: Record<string, MergeReviewDecision>, mapId?: string) {
    return this.request<MergeReviewProgress>(`/admin/merge-requests/${encodeURIComponent(id)}/decisions`, {
      method: 'PUT',
      body: JSON.stringify({ decisions, ...(mapId ? { mapId } : {}) }),
    })
  }

  closeMergeReview(id: string, decisions: Record<string, MergeReviewDecision>, expectedMainRevision: number, mapId?: string) {
    return this.request<MergeReviewCloseResult>(`/admin/merge-requests/${encodeURIComponent(id)}/close`, {
      method: 'POST',
      body: JSON.stringify({ decisions, expectedMainRevision, ...(mapId ? { mapId } : {}) }),
    })
  }

  withdrawMergeRequest(id: string, mapId?: string) {
    const suffix = mapId ? `?mapId=${encodeURIComponent(mapId)}` : ''
    return this.request<MergeRequest>(`/survey/merge-requests/${encodeURIComponent(id)}/withdraw${suffix}`, {
      method: 'POST',
    })
  }

  comments(mergeRequestId: string) {
    return this.request<Record<string, unknown>[]>(`/survey/merge-requests/${encodeURIComponent(mergeRequestId)}/comments`)
  }

  addComment(mergeRequestId: string, body: string) {
    return this.request<Record<string, unknown>>(`/survey/merge-requests/${encodeURIComponent(mergeRequestId)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    })
  }

  createMergeRequest(title: string, description: string, datasetId = 'builtin-survey', mapId?: string) {
    return this.request<MergeRequest>('/survey/merge-requests', { method: 'POST', body: JSON.stringify({ datasetId, title, description, ...(mapId ? { mapId } : {}) }) })
  }

  publish(datasetId = 'builtin-survey', mapId?: string) {
    return this.request<Record<string, unknown>>('/survey/publish', { method: 'POST', body: JSON.stringify({ datasetId, ...(mapId ? { mapId } : {}) }) })
  }

  accounts() {
    return this.request<AdminAccount[]>('/admin/accounts')
  }

  createAccount(username: string, password: string, minecraftUuid?: string) {
    return this.request<AdminAccount>('/admin/accounts', { method: 'POST', body: JSON.stringify({ username, password, minecraftUuid }) })
  }

  updateAccount(id: string, payload: { status?: 'ACTIVE' | 'DISABLED'; password?: string; minecraftUuid?: string | null }) {
    return this.request<AdminAccount>(`/admin/accounts/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(payload) })
  }

  audit() {
    return this.request<AuditEvent[]>('/admin/audit')
  }

  auditPage(page = 0, pageSize = 50) {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.request<ApiPage<AuditEventSummary>>(`/admin/audit/page?${params.toString()}`)
  }

  publishRecords() {
    return this.request<PublishRecord[]>('/admin/publish-records')
  }

  surveyStyles(datasetId = 'builtin-survey') {
    return this.request<SurveyStyle[]>(`/admin/survey-styles?datasetId=${encodeURIComponent(datasetId)}`)
  }

  upsertSurveyStyle(style: SurveyStyle, datasetId = 'builtin-survey') {
    return this.request<SurveyStyle>('/admin/survey-styles', {
      method: 'PUT',
      body: JSON.stringify({ datasetId, style }),
    })
  }

  deleteSurveyStyle(styleId: string, replacementStyleId?: string, datasetId = 'builtin-survey') {
    const params = new URLSearchParams({ datasetId })
    if (replacementStyleId) params.set('replacementStyleId', replacementStyleId)
    return this.request<SurveyStyleDeleteResult>(`/admin/survey-styles/${encodeURIComponent(styleId)}?${params.toString()}`, {
      method: 'DELETE',
    })
  }

  /** Metadata list for administrator-owned PNG pattern resources. */
  patternAssets(datasetId = 'builtin-survey') {
    return this.request<PatternAsset[]>(`/admin/pattern-assets?datasetId=${encodeURIComponent(datasetId)}`)
  }

  patternAsset(assetId: string) {
    return this.request<PatternAsset>(`/admin/pattern-assets/${encodeURIComponent(assetId)}`)
  }

  uploadPatternAsset(payload: PatternAssetUpload, datasetId = 'builtin-survey') {
    return this.request<PatternAsset>('/admin/pattern-assets', {
      method: 'POST',
      body: JSON.stringify({
        datasetId,
        widthPx: payload.widthPx,
        heightPx: payload.heightPx,
        pngBase64: payload.base64Png,
        contentType: 'image/png',
      }),
    })
  }

  updatePatternAsset(assetId: string, payload: PatternAssetUpload, datasetId = 'builtin-survey') {
    return this.request<PatternAsset>(`/admin/pattern-assets/${encodeURIComponent(assetId)}`, {
      method: 'PUT',
      body: JSON.stringify({
        datasetId,
        widthPx: payload.widthPx,
        heightPx: payload.heightPx,
        pngBase64: payload.base64Png,
        contentType: 'image/png',
      }),
    })
  }

  deletePatternAsset(assetId: string, datasetId = 'builtin-survey') {
    return this.request<{ deleted: boolean }>(`/admin/pattern-assets/${encodeURIComponent(assetId)}?datasetId=${encodeURIComponent(datasetId)}`, { method: 'DELETE' })
  }

  /** Public, cacheable URL used by the public map and published previews. */
  publicResourceUrl(assetId: string) {
    return `${apiOrigin}/pattern-assets/${encodeURIComponent(assetId)}.png`
  }

  /** Protected source URL used only by administrator previews. */
  adminPatternAssetUrl(assetId: string) {
    return `${apiOrigin}/admin/pattern-assets/${encodeURIComponent(assetId)}.png`
  }

  patternAssetUrl(assetId: string) {
    return this.publicResourceUrl(assetId)
  }

}

export const apiClient = new ApiClient()

export const apiBaseUrl = apiOrigin
